// components/widgets/widget-divider.js
// ═══════════════════════════════════════════════════════════
// WIDGET DIVIDER — drag-to-resize between two widgets in a row
// ═══════════════════════════════════════════════════════════
// Only interactive in edit mode. Sits in the 24px gap between
// two widgets within the same row. Drag horizontally to
// reallocate share from one widget to the other.
// ═══════════════════════════════════════════════════════════

import { useCallback, useRef } from 'react';

const DEFAULT_ROW_WIDTH = 1200;

export default function WidgetDivider({
  editing,
  rowId,
  leftKey,
  rightKey,
  containerRef,
  onAdjust,
}) {
  const dragStartRef = useRef(null);
  const accumulatedDeltaRef = useRef(0);

  const handlePointerDown = useCallback((e) => {
    if (!editing) return;
    e.preventDefault();
    const containerWidth = containerRef?.current?.offsetWidth || DEFAULT_ROW_WIDTH;
    const pxPerShare = containerWidth / 12;
    dragStartRef.current = { x: e.clientX, pxPerShare };
    accumulatedDeltaRef.current = 0;
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [editing, containerRef]);

  const handlePointerMove = useCallback((e) => {
    if (!dragStartRef.current) return;
    const { x, pxPerShare } = dragStartRef.current;
    const deltaPx = e.clientX - x;
    const targetShareDelta = Math.round(deltaPx / pxPerShare);
    if (targetShareDelta !== accumulatedDeltaRef.current) {
      const emit = targetShareDelta - accumulatedDeltaRef.current;
      accumulatedDeltaRef.current = targetShareDelta;
      if (onAdjust) onAdjust(rowId, leftKey, rightKey, emit);
    }
  }, [onAdjust, rowId, leftKey, rightKey]);

  const handlePointerUp = useCallback((e) => {
    if (dragStartRef.current) {
      dragStartRef.current = null;
      accumulatedDeltaRef.current = 0;
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* */ }
    }
  }, []);

  if (!editing) {
    return <div className="ws-widget-divider is-static" aria-hidden />;
  }

  return (
    <div
      className="ws-widget-divider is-editable"
      role="separator"
      aria-valuetext={`Resize between ${leftKey} and ${rightKey}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div className="ws-widget-divider-grip" aria-hidden>
        <span />
        <span />
      </div>
    </div>
  );
}
